import { clearSession, getAccessToken } from './session';

const getApiBaseUrl = (): string => process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:3001';

class ApiError extends Error {
  readonly precheck?: {
    pass?: boolean;
    failures?: Array<{ check?: string; code?: string; reason?: string; action_hint?: string }>;
    latency_ms?: number;
  } | null;

  constructor(
    message: string,
    public readonly status: number,
    precheck?: {
      pass?: boolean;
      failures?: Array<{ check?: string; code?: string; reason?: string; action_hint?: string }>;
      latency_ms?: number;
    } | null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.precheck = precheck || null;
  }
}

type ApiListResponse<T> = { data: T[] };
type ApiItemResponse<T> = { data: T };

export type ExpenseEventStatus = 'DRAFT' | 'PENDING' | 'PARTIALLY_APPROVED' | 'FULLY_APPROVED' | 'REJECTED' | 'SETTLED';
export type ExpenseSplitStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'AUTO_APPROVED';
export type SplitStrategy = 'EQUAL' | 'PERCENTAGE' | 'EXACT' | 'BY_HEADCOUNT';
export type ApprovalDecision = 'PENDING' | 'APPROVED' | 'REJECTED';
export type ApprovalActorType = 'MANAGER' | 'FINANCE';

export type ExpensePaymentRecord = {
  id: string;
  expense_event_id: string;
  paid_by_employee_id: string;
  amount_paid: number;
  payment_method?: string | null;
  external_payment_ref?: string | null;
  created_at?: string;
};

export type ExpenseSplitRecord = {
  id: string;
  expense_event_id: string;
  beneficiary_employee_id: string;
  department?: string | null;
  manager_id?: string | null;
  split_amount: number;
  split_pct?: number | null;
  status: ExpenseSplitStatus;
  created_at?: string;
};

export type SplitApprovalRecord = {
  id: string;
  expense_split_id: string;
  approver_id: string;
  approver_type: ApprovalActorType;
  decision: ApprovalDecision;
  decision_comment?: string | null;
  decided_at?: string | null;
  sla_due_at?: string | null;
  sequence_no: number;
  created_at?: string;
};

export type ExpenseEventRecord = {
  id: string;
  company_id: string;
  trip_id: string;
  category?: string | null;
  merchant?: string | null;
  notes?: string | null;
  total_amount: number;
  currency: string;
  exchange_rate_snapshot: number;
  base_amount: number;
  split_strategy: SplitStrategy;
  aggregate_status: ExpenseEventStatus;
  created_by: string;
  created_at?: string;
  updated_at?: string;
  payments?: ExpensePaymentRecord[];
  splits?: ExpenseSplitRecord[];
  approvals?: SplitApprovalRecord[];
  trust_signals?: {
    flagged: boolean;
    risk_score: number;
    generated_at?: string;
    signals: Array<{
      code: string;
      severity: 'high' | 'medium' | 'low';
      title: string;
      explanation: string;
      evidence?: Record<string, unknown>;
    }>;
  } | null;
  gst_profile?: {
    is_gst_applicable: boolean;
    vendor_gstin?: string | null;
    invoice_number?: string | null;
    invoice_date?: string | null;
    taxable_amount?: number;
    cgst_amount?: number;
    sgst_amount?: number;
    igst_amount?: number;
    total_gst_amount?: number;
  } | null;
  receipt_profile?: {
    receipt_requirement_status: 'NOT_REQUIRED' | 'OPTIONAL' | 'REQUIRED';
    processing_status?: 'NOT_UPLOADED' | 'UPLOADED' | 'PROCESSING' | 'PARSED' | 'FAILED';
    receipt_url?: string | null;
    receipt_filename?: string | null;
    receipt_mime_type?: string | null;
    receipt_metadata?: {
      extracted_amount?: number | null;
      extracted_vendor?: string | null;
      extracted_date?: string | null;
      gstin_detected?: string | null;
      confidence_score?: number | null;
    } | null;
    mismatch_flags?: string[] | null;
  } | null;
};

export type ManagerPendingSplitRow = {
  split_id: string;
  expense_event_id: string;
  trip_id: string;
  employee_id: string;
  employee_name?: string | null;
  manager_name?: string | null;
  department?: string | null;
  category?: string | null;
  merchant?: string | null;
  policy_rule?: string | null;
  split_amount: number;
  status: ExpenseSplitStatus;
  sla_due_at?: string | null;
  acting_approver_id?: string | null;
  acting_approver_name?: string | null;
  is_delegated?: boolean;
};

export type ReimbursementSummary = {
  trip_id: string;
  payable_total: number;
  risk_total: number;
  reimbursed_total: number;
  pending_entries: number;
  total_paid?: number;
};

export type ReimbursementEntry = {
  id: string;
  company_id: string;
  expense_event_id: string;
  payer_employee_id: string;
  payer_name?: string | null;
  payer_department?: string | null;
  trip_id?: string | null;
  reimbursable_amount: number;
  rejected_risk_amount: number;
  payroll_batch_ref?: string | null;
  status: 'PENDING' | 'SCHEDULED' | 'PAID' | 'FAILED' | 'CANCELLED';
  processed_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type DepartmentSpendReportRow = {
  department: string;
  approved_amount: number;
  pending_amount: number;
  rejected_amount: number;
  total_amount: number;
  split_count: number;
  employee_count: number;
};

export type ApprovalSlaReport = {
  total: number;
  pending: number;
  overdue: number;
  completion_rate: number;
  avg_response_hours: number;
};

export type ApprovalSlaBreachRow = {
  split_id: string;
  trip_id?: string | null;
  expense_event_id?: string | null;
  employee_name?: string | null;
  manager_name?: string | null;
  department?: string | null;
  category?: string | null;
  merchant?: string | null;
  split_amount: number;
  sla_due_at?: string | null;
  overdue_hours: number;
  approver_type?: string | null;
};

export type ReimbursementRiskReport = {
  total_approved_amount: number;
  total_rejected_amount: number;
  at_risk_amount: number;
  event_count: number;
};

export type ApprovalRuleRecord = {
  id: string;
  company_id: string;
  department?: string | null;
  category?: string | null;
  threshold_amount: number;
  requires_manager: boolean;
  requires_finance: boolean;
  auto_approve_below_threshold: boolean;
  sla_hours?: number | null;
  is_active: boolean;
  owner_name?: string | null;
  last_triggered_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type ReconciliationExceptionRow = {
  expense_event_id: string;
  trip_id?: string | null;
  category?: string | null;
  merchant?: string | null;
  aggregate_status?: string | null;
  base_amount: number;
  split_total: number;
  delta: number;
  created_at?: string | null;
};

export type ApprovalDelegationRecord = {
  id: string;
  company_id: string;
  approver_id: string;
  delegate_approver_id: string;
  start_at: string;
  end_at?: string | null;
  reason?: string | null;
  is_active: boolean;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  approver_name?: string | null;
  delegate_approver_name?: string | null;
  created_by_name?: string | null;
};

export type TrustConfigRecord = {
  duplicate_window_hours: number;
  odd_hour_start: number;
  odd_hour_end: number;
  high_deviation_multiplier: number;
  high_deviation_min_delta: number;
  baseline_lookback_days: number;
  minimum_history_samples: number;
};

export type ApprovalContinuityConfigRecord = {
  backup_approver_id?: string | null;
  auto_escalation_hours: number;
};

export type OpsMetricsSnapshot = {
  started_at: string;
  requests_total: number;
  by_path: Record<string, number>;
  by_status: Record<string, number>;
  latency_ms: {
    min: number | null;
    max: number;
    avg: number;
    samples: number;
  };
};

export type OpsAlert = {
  code: string;
  severity: 'high' | 'medium' | 'low';
  message: string;
};

export type NotificationRecord = {
  id: string;
  company_id: string;
  recipient_id: string;
  type: 'PENDING_APPROVAL' | 'SLA_BREACH' | 'POLICY_VIOLATION' | 'SYSTEM';
  title: string;
  message?: string | null;
  metadata?: Record<string, unknown> | null;
  is_read: boolean;
  read_at?: string | null;
  email_delivery_status?: 'queued' | 'sent' | 'failed' | 'skipped';
  email_delivery_error?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type NotificationPreferenceRecord = {
  company_id: string;
  recipient_id: string;
  type: 'PENDING_APPROVAL' | 'SLA_BREACH' | 'POLICY_VIOLATION';
  in_app_enabled: boolean;
  email_enabled: boolean;
};

export type ControlTowerStreamEvent = {
  company_id: string;
  type: string;
  payload?: Record<string, unknown>;
  occurred_at?: string;
};

export type NotificationStreamEvent = {
  company_id: string;
  recipient_id: string;
  type: string;
  payload?: Record<string, unknown>;
  occurred_at?: string;
};

export type CreateExpenseEventPayload = {
  trip_id: string;
  category?: string;
  merchant?: string;
  notes?: string;
  currency?: string;
  total_amount: number;
  split_strategy: SplitStrategy;
  payments: Array<{
    paid_by_employee_id: string;
    amount_paid: number;
    payment_method?: string;
    external_payment_ref?: string;
  }>;
  splits: Array<{
    beneficiary_employee_id: string;
    split_amount: number;
    split_pct?: number;
  }>;
  gst?: {
    is_gst_applicable: boolean;
    vendor_gstin?: string;
    invoice_number?: string;
    invoice_date?: string;
    taxable_amount?: number;
    cgst_amount?: number;
    sgst_amount?: number;
    igst_amount?: number;
  };
  receipt_metadata?: {
    extracted_amount?: number;
    extracted_vendor?: string;
    extracted_date?: string;
    gstin_detected?: string;
    confidence_score?: number;
  };
};

export type UpiIntentRecord = {
  intent_id: string;
  transaction_ref: string;
  provider: string;
  status: string;
  intent_url: string;
  callback_url?: string;
  expires_at?: string;
  merchant_name?: string | null;
  merchant_id?: string | null;
};

export type UpiIntentStatusRecord = {
  intent_id: string;
  transaction_ref: string;
  provider: string;
  status: 'INITIATED' | 'PENDING_CONFIRMATION' | 'CONFIRMED' | 'FAILED' | string;
  external_transaction_id?: string | null;
  failure_reason?: string | null;
  expense_event_id?: string | null;
  expense_event?: ExpenseEventRecord | null;
  callback_url?: string;
  updated_at?: string;
  expires_at?: string;
  merchant_name?: string | null;
  merchant_id?: string | null;
};

export type GstReportRow = {
  expense_event_id: string;
  employee?: string | null;
  vendor_gstin?: string | null;
  invoice_number?: string | null;
  taxable_amount: number;
  gst_amount: number;
  category?: string | null;
  date?: string | null;
};

const buildUrl = (path: string, query?: Record<string, string | number | undefined>): string => {
  const url = new URL(`${getApiBaseUrl()}${path}`);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
};

export const getAdminGstReportDownloadUrl = (format: 'csv' | 'excel' = 'csv'): string => {
  const token = getAccessToken();
  if (!token) return '';
  return buildUrl('/api/admin/reports/gst', {
    format,
    access_token: token,
    per_page: 2000,
  });
};

export const subscribeControlTowerStream = (handlers: {
  onConnected?: () => void;
  onEvent?: (event: ControlTowerStreamEvent) => void;
  onHeartbeat?: () => void;
  onError?: () => void;
}): (() => void) => {
  const token = getAccessToken();
  if (!token) return () => {};

  const streamUrl = buildUrl('/api/admin/control-tower/stream', { access_token: token });
  const source = new EventSource(streamUrl);

  source.addEventListener('connected', () => {
    handlers.onConnected?.();
  });
  source.addEventListener('heartbeat', () => {
    handlers.onHeartbeat?.();
  });
  source.addEventListener('control_tower_update', (event) => {
    try {
      const payload = JSON.parse((event as MessageEvent).data || '{}') as ControlTowerStreamEvent;
      handlers.onEvent?.(payload);
    } catch (_) {
      handlers.onEvent?.({ company_id: '', type: 'UNKNOWN' });
    }
  });
  source.onerror = () => {
    handlers.onError?.();
  };

  return () => {
    source.close();
  };
};

export const subscribeNotificationStream = (handlers: {
  onConnected?: () => void;
  onEvent?: (event: NotificationStreamEvent) => void;
  onHeartbeat?: () => void;
  onError?: () => void;
}): (() => void) => {
  const token = getAccessToken();
  if (!token) return () => {};

  const streamUrl = buildUrl('/api/notifications/stream', { access_token: token });
  const source = new EventSource(streamUrl);

  source.addEventListener('connected', () => {
    handlers.onConnected?.();
  });
  source.addEventListener('heartbeat', () => {
    handlers.onHeartbeat?.();
  });
  source.addEventListener('notification_update', (event) => {
    try {
      const payload = JSON.parse((event as MessageEvent).data || '{}') as NotificationStreamEvent;
      handlers.onEvent?.(payload);
    } catch (_) {
      handlers.onEvent?.({
        company_id: '',
        recipient_id: '',
        type: 'UNKNOWN',
      });
    }
  });
  source.onerror = () => {
    handlers.onError?.();
  };

  return () => {
    source.close();
  };
};

const request = async <T>(
  path: string,
  options?: RequestInit,
  query?: Record<string, string | number | undefined>,
): Promise<T> => {
  const token = getAccessToken();
  if (!token) {
    throw new ApiError('You are not logged in. Please sign in first.', 401);
  }

  const response = await fetch(buildUrl(path, query), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers || {}),
    },
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch (_) {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 401) {
      clearSession();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.replace('/login');
      }
    }
    const precheck = payload && typeof payload === 'object' && payload !== null && 'precheck' in payload
      ? (payload as { precheck?: ApiError['precheck'] }).precheck || null
      : null;
    const firstFailureReason =
      precheck?.failures && precheck.failures.length > 0
        ? String(precheck.failures[0]?.reason || '')
        : '';
    const message =
      firstFailureReason
      || (payload && typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed with status ${response.status}`);
    throw new ApiError(message, response.status, precheck);
  }

  return payload as T;
};

export const createExpenseEvent = async (payload: CreateExpenseEventPayload): Promise<ExpenseEventRecord> => {
  const response = await request<ApiItemResponse<ExpenseEventRecord>>('/api/expenses/events', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const getExpenseEvents = async (params?: {
  tripId?: string;
  status?: ExpenseEventStatus;
  page?: number;
  perPage?: number;
}): Promise<ExpenseEventRecord[]> => {
  const response = await request<ApiListResponse<ExpenseEventRecord>>(
    '/api/expenses/events',
    { method: 'GET' },
    {
      trip_id: params?.tripId,
      status: params?.status,
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 200,
    },
  );
  return response.data || [];
};

export const getExpenseEventById = async (expenseEventId: string): Promise<ExpenseEventRecord> => {
  const response = await request<ApiItemResponse<ExpenseEventRecord>>(`/api/expenses/events/${expenseEventId}`, {
    method: 'GET',
  });
  return response.data;
};

export const createUpiIntent = async (payload: {
  trip_id: string;
  category: string;
  amount: number;
  notes?: string;
  payment_method?: string;
  merchant_name?: string;
  merchant_id?: string;
  merchant_lat?: number;
  merchant_lng?: number;
  mcc_code?: string;
  expense_at?: string;
  calendar_events?: string[];
}): Promise<UpiIntentRecord> => {
  const response = await request<ApiItemResponse<UpiIntentRecord>>('/api/payments/upi-intents', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const getUpiIntentStatus = async (intentId: string): Promise<UpiIntentStatusRecord> => {
  const response = await request<ApiItemResponse<UpiIntentStatusRecord>>(`/api/payments/upi-intents/${intentId}`, {
    method: 'GET',
  });
  return response.data;
};

export const mockConfirmUpiIntent = async (
  intentId: string,
  success: boolean = true,
): Promise<{ intent: UpiIntentStatusRecord }> => {
  const response = await request<ApiItemResponse<{ intent: UpiIntentStatusRecord }>>(`/api/payments/upi-intents/${intentId}/mock-confirm`, {
    method: 'POST',
    body: JSON.stringify({ success }),
  });
  return response.data;
};

export const uploadExpenseReceipt = async (
  expenseEventId: string,
  payload: {
    file_name: string;
    content_type: string;
    data_base64: string;
    receipt_metadata?: {
      extracted_amount?: number;
      extracted_vendor?: string;
      extracted_date?: string;
      gstin_detected?: string;
      confidence_score?: number;
    };
  },
): Promise<NonNullable<ExpenseEventRecord['receipt_profile']>> => {
  const response = await request<ApiItemResponse<NonNullable<ExpenseEventRecord['receipt_profile']>>>(`/api/expenses/events/${expenseEventId}/receipts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const getAdminGstReport = async (params?: {
  fromDate?: string;
  toDate?: string;
  page?: number;
  perPage?: number;
}): Promise<GstReportRow[]> => {
  const response = await request<ApiListResponse<GstReportRow>>(
    '/api/admin/reports/gst',
    { method: 'GET' },
    {
      from_date: params?.fromDate,
      to_date: params?.toDate,
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 500,
    },
  );
  return response.data || [];
};

export const getManagerPendingSplits = async (params?: {
  page?: number;
  perPage?: number;
  department?: string;
  category?: string;
}): Promise<ManagerPendingSplitRow[]> => {
  const response = await request<ApiListResponse<ManagerPendingSplitRow>>(
    '/api/managers/me/pending-splits',
    { method: 'GET' },
    {
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 200,
      department: params?.department,
      category: params?.category,
    },
  );
  return response.data || [];
};

export const approveExpenseSplit = async (splitId: string, comment?: string): Promise<ExpenseSplitRecord> => {
  const response = await request<ApiItemResponse<ExpenseSplitRecord>>(`/api/expense-splits/${splitId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ comment: comment || '' }),
  });
  return response.data;
};

export const rejectExpenseSplit = async (splitId: string, comment?: string): Promise<ExpenseSplitRecord> => {
  const response = await request<ApiItemResponse<ExpenseSplitRecord>>(`/api/expense-splits/${splitId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ comment: comment || '' }),
  });
  return response.data;
};

export const getTripReimbursementSummary = async (tripId: string): Promise<ReimbursementSummary> => {
  const response = await request<ApiItemResponse<ReimbursementSummary>>(`/api/trips/${tripId}/reimbursement-summary`, {
    method: 'GET',
  });
  return response.data;
};

export const getReimbursements = async (params?: {
  status?: ReimbursementEntry['status'];
  tripId?: string;
  employeeId?: string;
  page?: number;
  perPage?: number;
}): Promise<ReimbursementEntry[]> => {
  const response = await request<ApiListResponse<ReimbursementEntry>>(
    '/api/reimbursements',
    { method: 'GET' },
    {
      status: params?.status,
      trip_id: params?.tripId,
      employee_id: params?.employeeId,
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 200,
    },
  );
  return response.data || [];
};

export const getAdminDepartmentSpendReport = async (): Promise<DepartmentSpendReportRow[]> => {
  const response = await request<ApiListResponse<DepartmentSpendReportRow>>('/api/admin/reports/department-spend', {
    method: 'GET',
  });
  return response.data || [];
};

export const getAdminApprovalSlaReport = async (): Promise<ApprovalSlaReport> => {
  const response = await request<ApiItemResponse<ApprovalSlaReport>>('/api/admin/reports/approval-sla', {
    method: 'GET',
  });
  return response.data;
};

export const getAdminApprovalSlaBreaches = async (params?: {
  page?: number;
  perPage?: number;
}): Promise<ApprovalSlaBreachRow[]> => {
  const response = await request<ApiListResponse<ApprovalSlaBreachRow>>(
    '/api/admin/reports/approval-sla-breaches',
    { method: 'GET' },
    {
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 200,
    },
  );
  return response.data || [];
};

export const getAdminReimbursementRiskReport = async (): Promise<ReimbursementRiskReport> => {
  const response = await request<ApiItemResponse<ReimbursementRiskReport>>('/api/admin/reports/reimbursement-risk', {
    method: 'GET',
  });
  return response.data;
};

export const getApprovalRules = async (): Promise<ApprovalRuleRecord[]> => {
  const response = await request<ApiListResponse<ApprovalRuleRecord>>('/api/admin/approval-rules', { method: 'GET' });
  return response.data || [];
};

export const createApprovalRule = async (
  payload: Partial<ApprovalRuleRecord> & { threshold_amount: number },
): Promise<ApprovalRuleRecord> => {
  const response = await request<ApiItemResponse<ApprovalRuleRecord>>('/api/admin/approval-rules', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const updateApprovalRule = async (
  ruleId: string,
  payload: Partial<ApprovalRuleRecord>,
): Promise<ApprovalRuleRecord> => {
  const response = await request<ApiItemResponse<ApprovalRuleRecord>>(`/api/admin/approval-rules/${ruleId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const getApprovalDelegations = async (params?: {
  includeInactive?: boolean;
}): Promise<ApprovalDelegationRecord[]> => {
  const response = await request<ApiListResponse<ApprovalDelegationRecord>>(
    '/api/admin/approval-delegations',
    { method: 'GET' },
    {
      include_inactive: params?.includeInactive ? 'true' : undefined,
    },
  );
  return response.data || [];
};

export const createApprovalDelegation = async (payload: {
  approver_id: string;
  delegate_approver_id: string;
  start_at: string;
  end_at?: string | null;
  reason?: string;
  is_active?: boolean;
}): Promise<ApprovalDelegationRecord> => {
  const response = await request<ApiItemResponse<ApprovalDelegationRecord>>('/api/admin/approval-delegations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const updateApprovalDelegation = async (
  delegationId: string,
  payload: Partial<ApprovalDelegationRecord>,
): Promise<ApprovalDelegationRecord> => {
  const response = await request<ApiItemResponse<ApprovalDelegationRecord>>(`/api/admin/approval-delegations/${delegationId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const getTrustConfig = async (): Promise<TrustConfigRecord> => {
  const response = await request<ApiItemResponse<TrustConfigRecord>>('/api/admin/trust-config', {
    method: 'GET',
  });
  return response.data;
};

export const updateTrustConfig = async (payload: Partial<TrustConfigRecord>): Promise<TrustConfigRecord> => {
  const response = await request<ApiItemResponse<TrustConfigRecord>>('/api/admin/trust-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const getApprovalContinuityConfig = async (): Promise<ApprovalContinuityConfigRecord> => {
  const response = await request<ApiItemResponse<ApprovalContinuityConfigRecord>>('/api/admin/approval-continuity-config', {
    method: 'GET',
  });
  return response.data;
};

export const updateApprovalContinuityConfig = async (
  payload: Partial<ApprovalContinuityConfigRecord>,
): Promise<ApprovalContinuityConfigRecord> => {
  const response = await request<ApiItemResponse<ApprovalContinuityConfigRecord>>('/api/admin/approval-continuity-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const recomputeReimbursements = async (payload?: {
  trip_id?: string;
  expense_event_id?: string;
}): Promise<{ recomputed_events: number }> => {
  const response = await request<ApiItemResponse<{ recomputed_events: number }>>('/api/admin/jobs/recompute-reimbursements', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  });
  return response.data;
};

export const bulkDecisionExpenseSplits = async (payload: {
  split_ids: string[];
  decision: 'APPROVED' | 'REJECTED';
  comment?: string;
}): Promise<{ updated: number; skipped: number; errors: Array<{ split_id: string; error: string }> }> => {
  const response = await request<ApiItemResponse<{ updated: number; skipped: number; errors: Array<{ split_id: string; error: string }> }>>('/api/admin/expense-splits/bulk-decision', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const remindManagersForSplits = async (payload: {
  split_ids: string[];
  message?: string;
}): Promise<{ sent: number; skipped: number }> => {
  const response = await request<ApiItemResponse<{ sent: number; skipped: number }>>('/api/admin/expense-splits/remind', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const bulkUpdateReimbursementsStatus = async (payload: {
  reimbursement_ids: string[];
  status: 'PENDING' | 'SCHEDULED' | 'PAID' | 'FAILED' | 'CANCELLED';
}): Promise<{ updated: number }> => {
  const response = await request<ApiItemResponse<{ updated: number }>>('/api/admin/reimbursements/bulk-status', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const recomputeTripSettlements = async (tripId: string): Promise<{ settlements_created: number }> => {
  const response = await request<ApiItemResponse<{ settlements_created: number }>>(`/api/admin/jobs/trips/${tripId}/recompute-settlements`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return response.data;
};

export const runEscalationScan = async (): Promise<{ overdue_count: number; escalated_count?: number }> => {
  const response = await request<ApiItemResponse<{ overdue_count: number; escalated_count?: number }>>('/api/admin/jobs/escalation-scan', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return response.data;
};

export const getOpsMetrics = async (): Promise<OpsMetricsSnapshot> => {
  const response = await request<ApiItemResponse<OpsMetricsSnapshot>>('/api/admin/ops/metrics', { method: 'GET' });
  return response.data;
};

export const getOpsAlerts = async (): Promise<OpsAlert[]> => {
  const response = await request<ApiListResponse<OpsAlert>>('/api/admin/ops/alerts', { method: 'GET' });
  return response.data || [];
};

export const getMyNotifications = async (params?: {
  status?: 'all' | 'read' | 'unread';
  page?: number;
  perPage?: number;
}): Promise<NotificationRecord[]> => {
  const response = await request<ApiListResponse<NotificationRecord>>(
    '/api/notifications',
    { method: 'GET' },
    {
      status: params?.status && params.status !== 'all' ? params.status : undefined,
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 20,
    },
  );
  return response.data || [];
};

export const markMyNotificationRead = async (notificationId: string): Promise<NotificationRecord> => {
  const response = await request<ApiItemResponse<NotificationRecord>>(`/api/notifications/${notificationId}/read`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return response.data;
};

export const markAllMyNotificationsRead = async (): Promise<{ updated: number }> => {
  const response = await request<ApiItemResponse<{ updated: number }>>('/api/notifications/read-all', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return response.data;
};

export const getMyNotificationPreferences = async (): Promise<NotificationPreferenceRecord[]> => {
  const response = await request<ApiListResponse<NotificationPreferenceRecord>>('/api/notifications/preferences', {
    method: 'GET',
  });
  return response.data || [];
};

export const updateMyNotificationPreference = async (payload: {
  type: NotificationPreferenceRecord['type'];
  in_app_enabled: boolean;
  email_enabled: boolean;
}): Promise<NotificationPreferenceRecord> => {
  const response = await request<ApiItemResponse<NotificationPreferenceRecord>>('/api/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const getReconciliationReport = async (): Promise<{
  legacy_total: number;
  unified_total: number;
  delta: number;
  parity_ok: boolean;
}> => {
  const response = await request<ApiItemResponse<{
    legacy_total: number;
    unified_total: number;
    delta: number;
    parity_ok: boolean;
  }>>('/api/admin/reports/reconciliation', { method: 'GET' });
  return response.data;
};

export const getReconciliationExceptionsReport = async (params?: {
  page?: number;
  perPage?: number;
}): Promise<ReconciliationExceptionRow[]> => {
  const response = await request<ApiListResponse<ReconciliationExceptionRow>>(
    '/api/admin/reports/reconciliation-exceptions',
    { method: 'GET' },
    {
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 200,
    },
  );
  return response.data || [];
};

export const isExpenseApiError = (error: unknown): error is ApiError => error instanceof ApiError;
